import React, { useState, useMemo, useCallback } from 'react';
import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import { TableSkeleton } from '../../common/loading';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Link,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ColumnHeaderFilterIcon from '../../common/ColumnHeaderFilterIcon';
import SchoolIcon from '@mui/icons-material/School';
import { SEARCE_COLORS } from '../../../theme';
import type { CertificationRecord } from '../../../types/driInsights';
import { CheckboxColumnFilterMenu, DateRangeColumnFilterMenu } from '../../dri-insights/ColumnFilterMenu';
import { ColumnVisibilityMenu } from '../../table/ColumnVisibilityMenu';
import TableCsvExportButton from '../../table/TableCsvExportButton';
import { exportCertificationsTableToCsv } from './certificationsTableCsvExport';
import { TABLE_STYLES } from '../../../constants/tableStyles';

/** Default Status column selection (data defaults to active certs); do not show as “filter active” until user changes it. */
const DEFAULT_CERT_STATUS_SELECTION = ['Active'] as const;

function certStatusSelectionIsBaseline(selected: string[] | undefined): boolean {
  if (!selected?.length) return false;
  const a = [...selected].sort().join('\0');
  const b = [...DEFAULT_CERT_STATUS_SELECTION].sort().join('\0');
  return a === b;
}

export type CertSortColumn =
  | 'employee_name'
  | 'certification_name'
  | 'issue_date'
  | 'expiry_date'
  | 'certification_provider'
  | 'status'
  | null;

type CertColumnId = Exclude<CertSortColumn, null>;

const CERT_COLUMN_VISIBILITY_ITEMS: { id: CertColumnId; label: string; hideable?: boolean }[] = [
  { id: 'employee_name', label: 'Employee', hideable: false },
  { id: 'certification_name', label: 'Certification name' },
  { id: 'issue_date', label: 'Issue date' },
  { id: 'expiry_date', label: 'Expiry date' },
  { id: 'certification_provider', label: 'Provider' },
  { id: 'status', label: 'Status' },
];

const CERT_COLUMN_ORDER: CertColumnId[] = [
  'employee_name',
  'certification_name',
  'issue_date',
  'expiry_date',
  'certification_provider',
  'status',
];

export interface CertificationsTableProps {
  isLoading: boolean;
  /** Raw certifications data; table owns filtering, sorting, pagination. */
  data: CertificationRecord[];
  formatDate: (dateString: string | null | undefined) => string;
}

type CertFilterState = {
  selectedValues?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
};

/**
 * Certifications table with internal sort, filter, and pagination.
 */
const CertificationsTable: React.FC<CertificationsTableProps> = ({
  isLoading,
  data,
  formatDate,
}) => {
  const [certFilters, setCertFilters] = useState<Record<string, CertFilterState>>({
    status: { selectedValues: [...DEFAULT_CERT_STATUS_SELECTION] },
  });
  const [certColumnVisibility, setCertColumnVisibility] = useState<VisibilityState>({});
  const [certFilterAnchorEl, setCertFilterAnchorEl] = useState<Record<string, HTMLElement | null>>({});
  const [certTempSelectedValues, setCertTempSelectedValues] = useState<Record<string, string[]>>({});
  const [certFilterSearchText, setCertFilterSearchText] = useState<Record<string, string>>({});
  const [certSortColumn, setCertSortColumn] = useState<CertSortColumn>(null);
  const [certSortDirection, setCertSortDirection] = useState<'asc' | 'desc'>('asc');
  const [certPage, setCertPage] = useState(0);
  const [certRowsPerPage, setCertRowsPerPage] = useState(10);

  const getCertAvailableValues = (columnId: string): string[] => {
    if (!data || data.length === 0) return [];
    const valueSet = new Set<string>();

    // For certification_name, always show ALL unique certifications from every employee
    // regardless of other active filters so the full list is always available.
    if (columnId === 'certification_name') {
      data.forEach((cert: CertificationRecord) => {
        if (cert.certification_name) valueSet.add(cert.certification_name);
      });
      return Array.from(valueSet).sort();
    }

    const rowsForOptions = data.filter((cert: CertificationRecord) => {
      for (const [filterColId, filter] of Object.entries(certFilters)) {
        if (filterColId === columnId) continue;
        if (filter.selectedValues && filter.selectedValues.length > 0) {
          if (filterColId === 'employee_name' && !filter.selectedValues.includes(cert.employee_name)) return false;
          if (filterColId === 'certification_name' && !filter.selectedValues.includes(cert.certification_name)) return false;
          if (filterColId === 'certification_provider' && !filter.selectedValues.includes(cert.certification_provider)) return false;
          if (filterColId === 'status' && !filter.selectedValues.includes(cert.status)) return false;
        }
        if (filterColId === 'issue_date' && (filter.startDate || filter.endDate)) {
          if (!cert.issue_date) return false;
          const d = new Date(cert.issue_date);
          if (filter.startDate && d < filter.startDate) return false;
          if (filter.endDate) {
            const end = new Date(filter.endDate);
            end.setHours(23, 59, 59, 999);
            if (d > end) return false;
          }
        }
        if (filterColId === 'expiry_date' && (filter.startDate || filter.endDate)) {
          if (!cert.expiry_date) return false;
          const d = new Date(cert.expiry_date);
          if (filter.startDate && d < filter.startDate) return false;
          if (filter.endDate) {
            const end = new Date(filter.endDate);
            end.setHours(23, 59, 59, 999);
            if (d > end) return false;
          }
        }
      }
      return true;
    });
    rowsForOptions.forEach((cert: CertificationRecord) => {
      switch (columnId) {
        case 'employee_name':
          if (cert.employee_name) valueSet.add(cert.employee_name);
          break;
        case 'certification_provider':
          if (cert.certification_provider) valueSet.add(cert.certification_provider);
          break;
        case 'status':
          if (cert.status) valueSet.add(cert.status);
          break;
      }
    });
    return Array.from(valueSet).sort();
  };

  const filteredCertifications = useMemo(() => {
    return data.filter((cert: CertificationRecord) => {
      if (certFilters.employee_name?.selectedValues?.length) {
        if (!certFilters.employee_name.selectedValues.includes(cert.employee_name)) return false;
      }
      if (certFilters.certification_name?.selectedValues?.length) {
        if (!certFilters.certification_name.selectedValues.includes(cert.certification_name)) return false;
      }
      if (certFilters.certification_provider?.selectedValues?.length) {
        if (!certFilters.certification_provider.selectedValues.includes(cert.certification_provider)) return false;
      }
      if (certFilters.status?.selectedValues?.length) {
        if (!certFilters.status.selectedValues.includes(cert.status)) return false;
      }
      if (certFilters.issue_date) {
        const { startDate, endDate } = certFilters.issue_date;
        if (cert.issue_date) {
          const d = new Date(cert.issue_date);
          if (startDate && d < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (d > end) return false;
          }
        } else if (startDate || endDate) return false;
      }
      if (certFilters.expiry_date) {
        const { startDate, endDate } = certFilters.expiry_date;
        if (cert.expiry_date) {
          const d = new Date(cert.expiry_date);
          if (startDate && d < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (d > end) return false;
          }
        } else if (startDate || endDate) return false;
      }
      return true;
    });
  }, [data, certFilters]);

  const sortedCertifications = useMemo(() => {
    if (!certSortColumn) return filteredCertifications;
    return [...filteredCertifications].sort((a, b) => {
      let valA: string | number;
      let valB: string | number;
      switch (certSortColumn) {
        case 'employee_name':
          valA = a.employee_name;
          valB = b.employee_name;
          break;
        case 'certification_name':
          valA = a.certification_name;
          valB = b.certification_name;
          break;
        case 'certification_provider':
          valA = a.certification_provider;
          valB = b.certification_provider;
          break;
        case 'status':
          valA = a.status;
          valB = b.status;
          break;
        case 'issue_date':
          valA = a.issue_date ? new Date(a.issue_date).getTime() : 0;
          valB = b.issue_date ? new Date(b.issue_date).getTime() : 0;
          break;
        case 'expiry_date':
          valA = a.expiry_date ? new Date(a.expiry_date).getTime() : 0;
          valB = b.expiry_date ? new Date(b.expiry_date).getTime() : 0;
          break;
        default:
          return 0;
      }
      if (valA < valB) return certSortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return certSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCertifications, certSortColumn, certSortDirection]);

  const paginatedCertifications = useMemo(() => {
    const start = certPage * certRowsPerPage;
    return sortedCertifications.slice(start, start + certRowsPerPage);
  }, [sortedCertifications, certPage, certRowsPerPage]);

  const visibleCertColumnIds = useMemo(
    () => CERT_COLUMN_ORDER.filter((id) => certColumnVisibility[id] !== false),
    [certColumnVisibility]
  );

  const onCertColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>((updater) => {
    setCertColumnVisibility((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const hasActiveFilter = (columnId: string) => {
    const f = certFilters[columnId];
    if (!f) return false;
    if (columnId === 'status' && f.selectedValues?.length) {
      if (certStatusSelectionIsBaseline(f.selectedValues)) return false;
    }
    if (f.selectedValues?.length) return true;
    if (f.startDate || f.endDate) return true;
    return false;
  };

  const handleCertFilterClick = (e: React.MouseEvent<HTMLElement>, columnId: string) => {
    setCertFilterAnchorEl({ ...certFilterAnchorEl, [columnId]: e.currentTarget });
    if (certTempSelectedValues[columnId] === undefined) {
      const applied = certFilters[columnId]?.selectedValues || [];
      setCertTempSelectedValues({ ...certTempSelectedValues, [columnId]: applied });
    }
  };

  const handleCertSort = (columnId: string) => {
    if (certSortColumn === columnId) {
      setCertSortDirection(certSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setCertSortColumn(columnId as CertSortColumn);
      setCertSortDirection('asc');
    }
    setCertPage(0);
  };

  const handleCertFilterClose = (columnId: string) => {
    setCertFilterAnchorEl({ ...certFilterAnchorEl, [columnId]: null });
    const t = { ...certTempSelectedValues };
    delete t[columnId];
    setCertTempSelectedValues(t);
    const s = { ...certFilterSearchText };
    delete s[columnId];
    setCertFilterSearchText(s);
  };

  const handleCertCheckboxToggle = (columnId: string, value: string) => {
    const current = certTempSelectedValues[columnId] || [];
    const applied = certFilters[columnId]?.selectedValues || [];
    const base = current.length > 0 ? current : applied;
    const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
    setCertTempSelectedValues({ ...certTempSelectedValues, [columnId]: next });
  };

  const handleCertApplyFilter = (columnId: string) => {
    const selected = certTempSelectedValues[columnId] || [];
    setCertFilters({
      ...certFilters,
      [columnId]: { selectedValues: selected.length > 0 ? selected : undefined },
    });
    const t = { ...certTempSelectedValues };
    delete t[columnId];
    setCertTempSelectedValues(t);
    const s = { ...certFilterSearchText };
    delete s[columnId];
    setCertFilterSearchText(s);
    handleCertFilterClose(columnId);
  };

  const handleCertSelectAll = (columnId: string, values: string[]) => {
    setCertTempSelectedValues({ ...certTempSelectedValues, [columnId]: values });
  };

  const handleCertClearAll = (columnId: string) => {
    setCertTempSelectedValues({ ...certTempSelectedValues, [columnId]: [] });
  };

  const renderCertFilterMenu = (columnId: string, columnLabel: string, availableValues: string[]) => {
    const anchorEl = certFilterAnchorEl[columnId];
    const open = Boolean(anchorEl);
    const filter = certFilters[columnId] || {};

    if (columnId === 'issue_date' || columnId === 'expiry_date') {
      const startDate = filter.startDate ?? null;
      const endDate = filter.endDate ?? null;
      return (
        <DateRangeColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => handleCertFilterClose(columnId)}
          columnLabel={columnLabel}
          startDate={startDate}
          endDate={endDate}
          onDateFilterApply={(s, e) => {
            setCertFilters({ ...certFilters, [columnId]: { ...filter, startDate: s, endDate: e } });
          }}
          hasFilter={!!(startDate || endDate)}
          onClearFilter={() => setCertFilters({ ...certFilters, [columnId]: { startDate: null, endDate: null } })}
        />
      );
    }

    const hasFilter = !!(filter.selectedValues && filter.selectedValues.length > 0);
    const currentTemp = certTempSelectedValues[columnId];
    const currentApplied = filter.selectedValues || [];
    const selectedValues = currentTemp !== undefined ? currentTemp : currentApplied;
    const searchText = certFilterSearchText[columnId] || '';
    const filteredValues = availableValues.filter((v) =>
      v.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
      <CheckboxColumnFilterMenu
        open={open}
        anchorEl={anchorEl}
        onClose={() => handleCertFilterClose(columnId)}
        columnLabel={columnLabel}
        options={filteredValues}
        selectedValues={selectedValues}
        onToggle={(v) => handleCertCheckboxToggle(columnId, v)}
        searchText={searchText}
        onSearchTextChange={(v) => setCertFilterSearchText({ ...certFilterSearchText, [columnId]: v })}
        onSelectAll={() => handleCertSelectAll(columnId, filteredValues)}
        onClearAll={() => handleCertClearAll(columnId)}
        onApply={() => handleCertApplyFilter(columnId)}
        hasFilter={hasFilter}
        onClearFilter={() => {
          setCertFilters({ ...certFilters, [columnId]: { selectedValues: undefined } });
          const t = { ...certTempSelectedValues };
          delete t[columnId];
          setCertTempSelectedValues(t);
          const s = { ...certFilterSearchText };
          delete s[columnId];
          setCertFilterSearchText(s);
        }}
      />
    );
  };

  const rows = paginatedCertifications;
  const totalCount = filteredCertifications.length;
  const page = certPage;
  const rowsPerPage = certRowsPerPage;
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        overflow: 'hidden',
        
        backgroundColor: SEARCE_COLORS.white,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          px: 2,
          ...TABLE_STYLES.tableToolbarTop,
        }}
      >
        <TableCsvExportButton
          disabled={sortedCertifications.length === 0}
          onClick={() =>
            exportCertificationsTableToCsv({
              rows: sortedCertifications,
              columnVisibility: certColumnVisibility,
              formatDate,
            })
          }
        />
        <ColumnVisibilityMenu
          items={CERT_COLUMN_VISIBILITY_ITEMS}
          columnVisibility={certColumnVisibility}
          onColumnVisibilityChange={onCertColumnVisibilityChange}
          triggerLabel="Toggle Columns"
        />
      </Box>

      <TableContainer sx={{ flex: 1, overflow: 'auto', maxHeight: 450 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: SEARCE_COLORS.primary }}>
              {visibleCertColumnIds.map((colId) => {
                const headerCellSx = {
                  backgroundColor: SEARCE_COLORS.primary,
                  fontWeight: 600,
                  color: SEARCE_COLORS.white,
                  fontSize: '0.75rem',
                  py: 1,
                  px: 1.5,
                  ...(colId === 'issue_date' || colId === 'expiry_date' ? { minWidth: '120px' } : {}),
                };
                const filterIcon = (columnKey: CertColumnId) => (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCertFilterClick(e, columnKey);
                    }}
                    sx={{
                      color: hasActiveFilter(columnKey) ? SEARCE_COLORS.white : 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      padding: '4px',
                    }}
                  >
                    <ColumnHeaderFilterIcon isFilterActive={hasActiveFilter(columnKey)} fontSize="small" />
                  </IconButton>
                );
                const menuLabel =
                  colId === 'employee_name'
                    ? 'Employee'
                    : colId === 'certification_name'
                      ? 'Certification Name'
                      : colId === 'issue_date'
                        ? 'Issue Date'
                        : colId === 'expiry_date'
                          ? 'Expiry Date'
                          : colId === 'certification_provider'
                            ? 'Provider'
                            : 'Status';
                const headerText =
                  colId === 'employee_name'
                    ? 'Employee'
                    : colId === 'certification_name'
                      ? 'Certification Name'
                      : colId === 'issue_date'
                        ? 'Issue Date'
                        : colId === 'expiry_date'
                          ? 'Expiry Date'
                          : colId === 'certification_provider'
                            ? 'Provider'
                            : 'Status';

                if (colId === 'issue_date' || colId === 'expiry_date') {
                  return (
                    <TableCell key={colId} sx={headerCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                        <Box component="span" sx={{ cursor: 'pointer' }} onClick={() => handleCertSort(colId)}>
                          {headerText}
                        </Box>
                        {filterIcon(colId)}
                      </Box>
                      {renderCertFilterMenu(colId, menuLabel, getCertAvailableValues(colId))}
                    </TableCell>
                  );
                }

                return (
                  <TableCell key={colId} sx={headerCellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                      {headerText}
                      <IconButton
                        size="small"
                        onClick={(e) => handleCertFilterClick(e, colId)}
                        sx={{
                          color: hasActiveFilter(colId) ? SEARCE_COLORS.white : 'rgba(255, 255, 255, 0.7)',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                          padding: '4px',
                        }}
                      >
                        <ColumnHeaderFilterIcon isFilterActive={hasActiveFilter(colId)} fontSize="small" />
                      </IconButton>
                    </Box>
                    {renderCertFilterMenu(colId, menuLabel, getCertAvailableValues(colId))}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={Math.max(1, visibleCertColumnIds.length)} sx={{ textAlign: 'center', py: 6 }}>
                  <TableSkeleton rows={8} columns={Math.max(1, visibleCertColumnIds.length)} rowHeight={32} />
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((cert, index) => (
                <TableRow
                  key={`${cert.employee_email}-${cert.certification_name}-${index}`}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(SEARCE_COLORS.primary, 0.05),
                    },
                  }}
                >
                  {visibleCertColumnIds.map((colId) => {
                    if (colId === 'employee_name') {
                      return (
                        <TableCell key={colId} sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5, fontWeight: 500 }}>
                          {cert.employee_name}
                        </TableCell>
                      );
                    }
                    if (colId === 'certification_name') {
                      return (
                        <TableCell key={colId} sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5 }}>
                          <Tooltip title={cert.certification_name} arrow>
                            <Box
                              component="span"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '200px',
                              }}
                            >
                              {cert.certification_name}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    }
                    if (colId === 'issue_date') {
                      return (
                        <TableCell key={colId} sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5 }}>
                          {formatDate(cert.issue_date)}
                        </TableCell>
                      );
                    }
                    if (colId === 'expiry_date') {
                      return (
                        <TableCell key={colId} sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5 }}>
                          {formatDate(cert.expiry_date)}
                        </TableCell>
                      );
                    }
                    if (colId === 'certification_provider') {
                      return (
                        <TableCell key={colId} sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5 }}>
                          <Tooltip title={cert.certification_provider} arrow>
                            <Box
                              component="span"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '150px',
                              }}
                            >
                              {cert.certification_provider}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    }
                    if (colId === 'status') {
                      return (
                        <TableCell key={colId} sx={{ fontSize: '0.75rem', py: 0.75, px: 1.5 }}>
                          {cert.public_link ? (
                            <Link
                              href={cert.public_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textDecoration: 'none' }}
                            >
                              <Chip
                                label={cert.status}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    cert.status === 'Active'
                                      ? `${SEARCE_COLORS.success}15`
                                      : cert.status === 'Expiring Soon'
                                        ? `${SEARCE_COLORS.warning}15`
                                        : `${SEARCE_COLORS.error}15`,
                                  color:
                                    cert.status === 'Active'
                                      ? SEARCE_COLORS.success
                                      : cert.status === 'Expiring Soon'
                                        ? SEARCE_COLORS.warning
                                        : SEARCE_COLORS.error,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: '24px',
                                  cursor: 'pointer',
                                }}
                              />
                            </Link>
                          ) : (
                            <Chip
                              label={cert.status}
                              size="small"
                              sx={{
                                backgroundColor:
                                  cert.status === 'Active'
                                    ? `${SEARCE_COLORS.success}15`
                                    : cert.status === 'Expiring Soon'
                                      ? `${SEARCE_COLORS.warning}15`
                                      : `${SEARCE_COLORS.error}15`,
                                color:
                                  cert.status === 'Active'
                                    ? SEARCE_COLORS.success
                                    : cert.status === 'Expiring Soon'
                                      ? SEARCE_COLORS.warning
                                      : SEARCE_COLORS.error,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '24px',
                              }}
                            />
                          )}
                        </TableCell>
                      );
                    }
                    return null;
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, visibleCertColumnIds.length)}
                  sx={{ textAlign: 'center', py: 6, color: SEARCE_COLORS.textSecondary }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <SchoolIcon sx={{ fontSize: 48, mb: 2, color: SEARCE_COLORS.neutral_300 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      No Certifications Found
                    </Typography>
                    <Typography variant="body2">No certifications found for the selected filters.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setCertPage(newPage)}
        onRowsPerPageChange={(e) => {
          setCertRowsPerPage(parseInt(e.target.value, 10));
          setCertPage(0);
        }}
        sx={{
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: SEARCE_COLORS.textSecondary,
            fontWeight: 500,
          },
          '& .MuiTablePagination-select': {
            color: SEARCE_COLORS.primary,
            fontWeight: 600,
          },
          '& .MuiIconButton-root': {
            color: SEARCE_COLORS.primary,
            '&:hover': {
              backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
            },
            '&.Mui-disabled': {
              color: SEARCE_COLORS.neutral_300,
            },
          },
        }}
      />
    </Paper>
  );
};

export default CertificationsTable;
